import { assert } from './assert.js';

// Pairs of the committed event and the undo event derived from its result. Undo pops a pair
// into redo; redo pops the original back out and the caller re-commits it, pushing a fresh pair
// from the fresh result. Any user commit clears redo, whether or not it produced a pair.

export interface UndoPair<TMutation> {
  readonly original: TMutation;
  readonly undo: TMutation;
}

const DEPTH_MAX = 10_000;

export class UndoStack<TMutation> {
  private readonly undoPairs: UndoPair<TMutation>[] = [];
  private readonly redoPairs: UndoPair<TMutation>[] = [];

  push(original: TMutation, undo: TMutation): void {
    this.undoPairs.push({ original, undo });
    if (this.undoPairs.length > DEPTH_MAX) this.undoPairs.shift();
    assert(this.undoPairs.length <= DEPTH_MAX, 'UndoStack: depth within bound');
    this.clearRedo();
  }

  popUndo(): UndoPair<TMutation> | undefined {
    const pair = this.undoPairs.pop();
    if (pair === undefined) return undefined;
    this.redoPairs.push(pair);
    assert(this.redoPairs.length <= DEPTH_MAX, 'UndoStack: redo depth within bound');
    return pair;
  }

  // The original comes back for re-commit; the caller pushes the fresh pair with pushRedone.
  popRedo(): UndoPair<TMutation> | undefined {
    return this.redoPairs.pop();
  }

  // A redo's fresh pair goes back on undo without touching the rest of redo, so a run of undos
  // can be redone one at a time.
  pushRedone(original: TMutation, undo: TMutation): void {
    this.undoPairs.push({ original, undo });
    assert(this.undoPairs.length <= DEPTH_MAX, 'UndoStack: depth within bound');
  }

  // Popped-then-failed leaves the stacks as they were.
  restoreUndo(pair: UndoPair<TMutation>): void {
    const moved = this.redoPairs.pop();
    assert(moved === pair, 'restoreUndo: the pair on top of redo is the one popped');
    this.undoPairs.push(pair);
  }

  restoreRedo(pair: UndoPair<TMutation>): void {
    this.redoPairs.push(pair);
  }

  clearRedo(): void {
    this.redoPairs.length = 0;
  }

  get canUndo(): boolean { return this.undoPairs.length > 0; }
  get canRedo(): boolean { return this.redoPairs.length > 0; }

  clear(): void {
    this.undoPairs.length = 0;
    this.redoPairs.length = 0;
  }
}
